import {
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
  UseRadioGroupProps,
  UseRadioGroupReturn,
  UseRadioProps,
  forwardRef,
  useRadio,
  useRadioGroup,
} from "@chakra-ui/react";
import { createContext, useContext, useMemo } from "react";

type RadioButtonGroupContextType = Pick<
  UseRadioGroupReturn,
  "getRadioProps"
> & {
  buttonProps?: ButtonProps;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const RadioButtonGroupContext = createContext<RadioButtonGroupContextType>({
  getRadioProps: () => ({}),
});

type RadioButtonProps = Omit<ButtonProps, "value"> & {
  value: UseRadioProps["value"];
};

export function RadioButton({ children, value, ...rest }: RadioButtonProps) {
  const { buttonProps, getRadioProps } = useContext(RadioButtonGroupContext);
  const radioProps = getRadioProps({ value });
  const { getInputProps, getCheckboxProps } = useRadio(radioProps);
  return (
    <Button as="label" {...getCheckboxProps({ ...buttonProps, ...rest })}>
      <input {...getInputProps()} />
      {children}
    </Button>
  );
}

export type RadioButtonGroupProps = UseRadioGroupProps &
  Omit<ButtonGroupProps, "onChange"> & {
    buttonProps?: ButtonProps;
    children: React.ReactNode;
  };

const RadioButtonGroup = forwardRef<RadioButtonGroupProps, "div">(
  // eslint-disable-next-line @typescript-eslint/unbound-method
  ({ buttonProps, children, onChange, ...rest }, ref) => {
    const { getRootProps, getRadioProps } = useRadioGroup({
      onChange,
      ...rest,
    });

    const radioButtonGroupContext = useMemo(
      () => ({ getRadioProps, buttonProps }),
      [getRadioProps, buttonProps],
    );

    return (
      <ButtonGroup
        ref={ref}
        variant="simpleSquare"
        colorScheme="grayMagentaButton"
        {...getRootProps(rest)}
      >
        <RadioButtonGroupContext.Provider value={radioButtonGroupContext}>
          {children}
        </RadioButtonGroupContext.Provider>
      </ButtonGroup>
    );
  },
);

export default RadioButtonGroup;

export const attachedOutlineButtonProps: Pick<
  RadioButtonGroupProps,
  "buttonProps" | "isAttached" | "size" | "variant" | "colorScheme"
> = {
  isAttached: true,
  size: "xs",
  variant: "outline",
  colorScheme: "lightPinkButton",
  buttonProps: {
    textTransform: "none",
    _checked: {
      backgroundColor: "lightPink",
      color: "white",
      borderColor: "magenta.dark",
    },
  },
};
